/**
 * Marketing Campaigns Dashboard
 */

import { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Tab,
  Tabs,
  LinearProgress
} from "@mui/material";
import {
  Add as AddIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Send as SendIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Analytics as AnalyticsIcon,
  Schedule as ScheduleIcon
} from "@mui/icons-material";

// Argon Dashboard components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

function MarketingCampaigns() {
  const [tabValue, setTabValue] = useState(0);
  const [emailCampaigns, setEmailCampaigns] = useState([]);
  const [smsCampaigns, setSmsCampaigns] = useState([]);
  const [contactLists, setContactLists] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [createEmailDialog, setCreateEmailDialog] = useState(false);
  const [createSmsDialog, setCreateSmsDialog] = useState(false);
  const [createListDialog, setCreateListDialog] = useState(false);
  
  // Form states
  const [emailForm, setEmailForm] = useState({
    name: "",
    subject: "",
    content: "",
    sender_email: "",
    sender_name: "",
    contact_list_id: "",
    send_immediately: false
  });
  
  const [smsForm, setSmsForm] = useState({
    name: "",
    message: "",
    contact_list_id: "",
    send_immediately: false
  });
  
  const [listForm, setListForm] = useState({
    name: "",
    description: "",
    list_type: "manual"
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load email campaigns
      const emailResponse = await fetch('/api/v1/marketing/email-campaigns/');
      const emailData = await emailResponse.json();
      setEmailCampaigns(emailData);
      
      // Load SMS campaigns
      const smsResponse = await fetch('/api/v1/marketing/sms-campaigns/');
      const smsData = await smsResponse.json();
      setSmsCampaigns(smsData);
      
      // Load contact lists
      const listsResponse = await fetch('/api/v1/marketing/contact-lists/');
      const listsData = await listsResponse.json();
      setContactLists(listsData);
      
    } catch (error) {
      console.error('Error loading campaigns:', error);
    }
    setLoading(false);
  };

  const handleCreateEmailCampaign = async () => {
    try {
      const response = await fetch('/api/v1/marketing/email-campaigns/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailForm)
      });
      
      if (response.ok) {
        setCreateEmailDialog(false);
        setEmailForm({
          name: "",
          subject: "",
          content: "",
          sender_email: "",
          sender_name: "",
          contact_list_id: "",
          send_immediately: false
        });
        loadData();
      }
    } catch (error) {
      console.error('Error creating email campaign:', error);
    }
  };

  const handleCreateSmsCampaign = async () => {
    try {
      const response = await fetch('/api/v1/marketing/sms-campaigns/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(smsForm)
      });
      
      if (response.ok) {
        setCreateSmsDialog(false);
        setSmsForm({
          name: "",
          message: "",
          contact_list_id: "",
          send_immediately: false
        });
        loadData();
      }
    } catch (error) {
      console.error('Error creating SMS campaign:', error);
    }
  };

  const handleCreateContactList = async () => {
    try {
      const response = await fetch('/api/v1/marketing/contact-lists/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(listForm)
      });
      
      if (response.ok) {
        setCreateListDialog(false);
        setListForm({
          name: "",
          description: "",
          list_type: "manual"
        });
        loadData();
      }
    } catch (error) {
      console.error('Error creating contact list:', error);
    }
  };

  const handleSendCampaign = async (campaignId, type) => {
    try {
      const response = await fetch(`/api/v1/marketing/${type}-campaigns/${campaignId}/send`, {
        method: 'POST'
      });
      
      if (response.ok) {
        loadData();
      }
    } catch (error) {
      console.error('Error sending campaign:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'success';
      case 'sending': return 'info';
      case 'draft': return 'secondary';
      case 'scheduled': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const renderEmailCampaigns = () => (
    <ArgonBox>
      <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <ArgonTypography variant="h5">Email Campaigns</ArgonTypography>
        <ArgonButton
          variant="gradient"
          color="info"
          startIcon={<AddIcon />}
          onClick={() => setCreateEmailDialog(true)}
        >
          Create Email Campaign
        </ArgonButton>
      </ArgonBox>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Campaign Name</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Recipients</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {emailCampaigns.map((campaign) => (
              <TableRow key={campaign.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {campaign.name}
                  </Typography>
                </TableCell>
                <TableCell>{campaign.subject}</TableCell>
                <TableCell>{campaign.total_recipients || 0}</TableCell>
                <TableCell>
                  <Chip 
                    label={campaign.status} 
                    color={getStatusColor(campaign.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {new Date(campaign.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  {campaign.status === 'draft' && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleSendCampaign(campaign.id, 'email')}
                    >
                      <SendIcon />
                    </IconButton>
                  )}
                  <IconButton size="small" color="info">
                    <AnalyticsIcon />
                  </IconButton>
                  <IconButton size="small" color="secondary">
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ArgonBox>
  );

  const renderSmsCampaigns = () => (
    <ArgonBox>
      <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <ArgonTypography variant="h5">SMS Campaigns</ArgonTypography>
        <ArgonButton
          variant="gradient"
          color="success"
          startIcon={<AddIcon />}
          onClick={() => setCreateSmsDialog(true)}
        >
          Create SMS Campaign
        </ArgonButton>
      </ArgonBox>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Campaign Name</TableCell>
              <TableCell>Message Preview</TableCell>
              <TableCell>Recipients</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {smsCampaigns.map((campaign) => (
              <TableRow key={campaign.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {campaign.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {campaign.message}
                  </Typography>
                </TableCell>
                <TableCell>{campaign.total_recipients || 0}</TableCell>
                <TableCell>
                  <Chip 
                    label={campaign.status} 
                    color={getStatusColor(campaign.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {new Date(campaign.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  {campaign.status === 'draft' && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleSendCampaign(campaign.id, 'sms')}
                    >
                      <SendIcon />
                    </IconButton>
                  )}
                  <IconButton size="small" color="info">
                    <AnalyticsIcon />
                  </IconButton>
                  <IconButton size="small" color="secondary">
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ArgonBox>
  );

  const renderContactLists = () => (
    <ArgonBox>
      <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <ArgonTypography variant="h5">Contact Lists</ArgonTypography>
        <ArgonButton
          variant="gradient"
          color="warning"
          startIcon={<AddIcon />}
          onClick={() => setCreateListDialog(true)}
        >
          Create Contact List
        </ArgonButton>
      </ArgonBox>
      
      <Grid container spacing={3}>
        {contactLists.map((list) => (
          <Grid item xs={12} md={6} lg={4} key={list.id}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {list.name}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {list.description}
                </Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                  <Typography variant="body2">
                    {list.contact_count} contacts
                  </Typography>
                  <Chip label={list.list_type} size="small" />
                </Box>
                <Box mt={2}>
                  <Button size="small" color="primary">
                    View Contacts
                  </Button>
                  <Button size="small" color="secondary">
                    Edit
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </ArgonBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <ArgonBox p={3}>
                  <ArgonTypography variant="h4" fontWeight="medium">
                    Marketing Campaigns
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="text">
                    Create and manage email and SMS campaigns
                  </ArgonTypography>
                </ArgonBox>
              </Card>
            </Grid>
          </Grid>
        </ArgonBox>

        {loading ? (
          <ArgonBox>
            <LinearProgress />
          </ArgonBox>
        ) : (
          <ArgonBox>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                <Tab label={`Email Campaigns (${emailCampaigns.length})`} icon={<EmailIcon />} />
                <Tab label={`SMS Campaigns (${smsCampaigns.length})`} icon={<SmsIcon />} />
                <Tab label={`Contact Lists (${contactLists.length})`} />
              </Tabs>
            </Box>
            
            <ArgonBox mt={3}>
              {tabValue === 0 && renderEmailCampaigns()}
              {tabValue === 1 && renderSmsCampaigns()}
              {tabValue === 2 && renderContactLists()}
            </ArgonBox>
          </ArgonBox>
        )}
      </ArgonBox>

      {/* Create Email Campaign Dialog */}
      <Dialog open={createEmailDialog} onClose={() => setCreateEmailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Email Campaign</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Campaign Name"
                value={emailForm.name}
                onChange={(e) => setEmailForm({...emailForm, name: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject Line"
                value={emailForm.subject}
                onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Sender Email"
                value={emailForm.sender_email}
                onChange={(e) => setEmailForm({...emailForm, sender_email: e.target.value})}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Sender Name"
                value={emailForm.sender_name}
                onChange={(e) => setEmailForm({...emailForm, sender_name: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Contact List</InputLabel>
                <Select
                  value={emailForm.contact_list_id}
                  onChange={(e) => setEmailForm({...emailForm, contact_list_id: e.target.value})}
                >
                  {contactLists.map((list) => (
                    <MenuItem key={list.id} value={list.id}>
                      {list.name} ({list.contact_count} contacts)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Email Content (HTML)"
                value={emailForm.content}
                onChange={(e) => setEmailForm({...emailForm, content: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateEmailDialog(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              setEmailForm({...emailForm, send_immediately: false});
              handleCreateEmailCampaign();
            }}
            variant="outlined"
          >
            Save Draft
          </Button>
          <Button 
            onClick={() => {
              setEmailForm({...emailForm, send_immediately: true});
              handleCreateEmailCampaign();
            }}
            variant="contained"
          >
            Send Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create SMS Campaign Dialog */}
      <Dialog open={createSmsDialog} onClose={() => setCreateSmsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create SMS Campaign</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Campaign Name"
                value={smsForm.name}
                onChange={(e) => setSmsForm({...smsForm, name: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Contact List</InputLabel>
                <Select
                  value={smsForm.contact_list_id}
                  onChange={(e) => setSmsForm({...smsForm, contact_list_id: e.target.value})}
                >
                  {contactLists.map((list) => (
                    <MenuItem key={list.id} value={list.id}>
                      {list.name} ({list.contact_count} contacts)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="SMS Message"
                value={smsForm.message}
                onChange={(e) => setSmsForm({...smsForm, message: e.target.value})}
                helperText={`${smsForm.message.length}/160 characters`}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateSmsDialog(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              setSmsForm({...smsForm, send_immediately: false});
              handleCreateSmsCampaign();
            }}
            variant="outlined"
          >
            Save Draft
          </Button>
          <Button 
            onClick={() => {
              setSmsForm({...smsForm, send_immediately: true});
              handleCreateSmsCampaign();
            }}
            variant="contained"
          >
            Send Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Contact List Dialog */}
      <Dialog open={createListDialog} onClose={() => setCreateListDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Contact List</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="List Name"
                value={listForm.name}
                onChange={(e) => setListForm({...listForm, name: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={listForm.description}
                onChange={(e) => setListForm({...listForm, description: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>List Type</InputLabel>
                <Select
                  value={listForm.list_type}
                  onChange={(e) => setListForm({...listForm, list_type: e.target.value})}
                >
                  <MenuItem value="manual">Manual</MenuItem>
                  <MenuItem value="automated">Automated</MenuItem>
                  <MenuItem value="segment">Segment</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateListDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateContactList} variant="contained">
            Create List
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default MarketingCampaigns;